import { useMemo } from 'react'

import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import { AddOnProductOptionGroup } from '@src/quince-site-common/src/domains/AddOnProductOptionGroup'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { getDisplayKey } from '@src/utils/i18n.utils'

import { getProductLinkWithSlug } from '../../molecules/productCard/productCard.helpers'

import SiblingProductButton from './siblingProductButton/SiblingProductButton'
import styles from './SiblingProductOptions.module.scss'

export enum SiblingProductOptionType {
  LINK = 'LINK',
  BUTTON = 'BUTTON',
}

interface SiblingProductOptionsProps {
  siblingProductOptionGroup: AddOnProductOptionGroup
  displayType: SiblingProductOptionType
  selectedProductOptions?: ProductVariantOption[]
  onSelect?: (val: string, productId?: number) => void
  selectedProductId?: number
  className?: string
  labelClassName?: string
  optionSelectorClassName?: string
  optionClassname?: string
  showNewPdpDesignV2?: boolean
  isMiniPDP?: boolean
}

export default function SiblingProductOptions({
  siblingProductOptionGroup,
  onSelect,
  displayType,
  selectedProductId,
  selectedProductOptions = [],
  className,
  labelClassName,
  optionSelectorClassName,
  optionClassname,
  showNewPdpDesignV2,
  isMiniPDP,
}: SiblingProductOptionsProps) {
  const isNewLocalisedFlow = useI18nPreviewContext()

  const selectedGroupItemLabel = useMemo(() => {
    const selectedOption = siblingProductOptionGroup.groupItems.find(groupItem => groupItem.spid === selectedProductId)
    return selectedOption
      ? getDisplayKey(
          selectedOption,
          'mergeOptionValueDisplayName',
          selectedOption.mergeOptionValue,
          isNewLocalisedFlow,
        )
      : ''
  }, [siblingProductOptionGroup.groupItems, selectedProductId])

  return (
    <div data-testid="sibling-product-options">
      <div className={className}>
        <div className={labelClassName}>
          <span>
            <span>
              {getDisplayKey(
                siblingProductOptionGroup,
                'groupOptionDisplayName',
                siblingProductOptionGroup.groupOptionName,
                isNewLocalisedFlow,
              )}
            </span>
            : <span className={styles.selectedGroupItemLabel}>{selectedGroupItemLabel}</span>
          </span>
        </div>
        <div className={optionSelectorClassName} role="group">
          {siblingProductOptionGroup.groupItems.map((groupItem, idx) => {
            return (
              <SiblingProductButton
                id={`sibling-product-option-${idx}`}
                key={groupItem.spid}
                value={
                  displayType === SiblingProductOptionType.LINK
                    ? getProductLinkWithSlug(groupItem.productHandle, selectedProductOptions, {
                        includeSize: true,
                        isMiniPDP,
                      })
                    : String(groupItem.spid)
                }
                displayName={getDisplayKey(
                  groupItem,
                  'mergeOptionValueDisplayName',
                  groupItem.mergeOptionValue,
                  isNewLocalisedFlow,
                )}
                isActive={groupItem.spid === selectedProductId}
                variant={displayType}
                onClickCallback={val => onSelect?.(val, groupItem.productId)}
                className={optionClassname}
                showNewPdpDesignV2={showNewPdpDesignV2}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
